import { useState } from "react";
import { api } from "@/lib/api";
import { WhatsappLogo, EnvelopeSimple, Phone, X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Kontakt-Zellen mit Inline-Nachtragen.
 * - Telefon-Zelle: klickbarer tel:-Link + WhatsApp-Icon (immer sichtbar)
 * - E-Mail-Zelle:  klickbarer mailto:-Link + Editier-Icon
 * - Fehlt der Kontakt, öffnet Klick auf das Icon eine kleine Inline-Erfassung.
 *   Nach Speichern wird direkt der WhatsApp-/E-Mail-Client geöffnet.
 */

function normalizePhoneForWa(raw) {
  const digits = String(raw || "").replace(/[^0-9]/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.startsWith("0"))  return "49" + digits.slice(1);
  return digits;
}

export function PhoneCell({ guest, onGuestUpdated }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal]   = useState(guest.phone || "");
  const [busy, setBusy] = useState(false);
  const hasPhone = !!guest.phone && guest.phone.trim() !== "";

  const openWa = (phone) => {
    const wa = normalizePhoneForWa(phone);
    if (!wa) { toast.error("Handynummer ungültig"); return; }
    window.open(`https://wa.me/${wa}`, "_blank", "noopener,noreferrer");
  };

  const save = async (openAfter) => {
    const v = val.trim();
    if (!v) { toast.error("Bitte Handynummer eingeben"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/guests/${guest.id}/contact`, { phone: v });
      toast.success("Handynummer gespeichert");
      onGuestUpdated?.(data);
      setEdit(false);
      if (openAfter === "wa") openWa(v);
      if (openAfter === "call") window.location.href = `tel:${v}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte nicht speichern");
    } finally { setBusy(false); }
  };

  if (edit) {
    return (
      <div className="inline-flex items-center gap-1" onClick={(e)=>e.stopPropagation()}>
        <input
          value={val}
          autoFocus
          onChange={(e)=>setVal(e.target.value)}
          onKeyDown={(e)=>{ if (e.key==="Enter") save("wa"); if (e.key==="Escape") setEdit(false); }}
          placeholder="+49 176 …"
          className="h-8 w-40 px-2 rounded-lg border border-primary/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid={`guest-phone-input-${guest.id}`}
          disabled={busy}
        />
        <button type="button" onClick={()=>save("wa")}
                disabled={busy}
                title="Speichern & WhatsApp öffnen"
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50"
                data-testid={`guest-phone-save-${guest.id}`}>
          <Check size={14} weight="bold"/>
        </button>
        <button type="button" onClick={()=>{ setEdit(false); setVal(guest.phone || ""); }}
                title="Abbrechen"
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition">
          <X size={13} weight="bold"/>
        </button>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2" onClick={(e)=>e.stopPropagation()}>
      {hasPhone ? (
        <a href={`tel:${guest.phone}`}
           className="hover:text-primary hover:underline"
           data-testid={`guest-phone-${guest.id}`}>{guest.phone}</a>
      ) : (
        <button type="button" onClick={()=>setEdit(true)}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary italic"
                data-testid={`guest-phone-add-${guest.id}`}>
          <Phone size={12} weight="bold"/> Nummer hinzufügen
        </button>
      )}
      <button
        type="button"
        onClick={() => { hasPhone ? openWa(guest.phone) : setEdit(true); }}
        title={hasPhone ? "WhatsApp-Chat öffnen" : "Nummer eintragen & Chat starten"}
        className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-600 hover:text-white transition"
        data-testid={`guest-whatsapp-${guest.id}`}>
        <WhatsappLogo size={14} weight="bold"/>
      </button>
    </div>
  );
}

export function EmailCell({ guest, onGuestUpdated }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal]   = useState(guest.email || "");
  const [busy, setBusy] = useState(false);
  const hasEmail = !!guest.email && guest.email.includes("@");

  const save = async () => {
    const v = val.trim();
    if (!v || !v.includes("@")) { toast.error("Bitte gültige E-Mail-Adresse eingeben"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/guests/${guest.id}/contact`, { email: v });
      toast.success("E-Mail-Adresse gespeichert");
      onGuestUpdated?.(data);
      setEdit(false);
      window.location.href = `mailto:${v}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte nicht speichern");
    } finally { setBusy(false); }
  };

  if (edit) {
    return (
      <div className="inline-flex items-center gap-1" onClick={(e)=>e.stopPropagation()}>
        <input
          value={val}
          autoFocus
          type="email"
          onChange={(e)=>setVal(e.target.value)}
          onKeyDown={(e)=>{ if (e.key==="Enter") save(); if (e.key==="Escape") setEdit(false); }}
          placeholder="max@musterhaus.de"
          className="h-8 w-56 px-2 rounded-lg border border-primary/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid={`guest-email-input-${guest.id}`}
          disabled={busy}
        />
        <button type="button" onClick={save}
                disabled={busy}
                title="Speichern & E-Mail öffnen"
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-primary text-white hover:bg-primary/90 transition disabled:opacity-50"
                data-testid={`guest-email-save-${guest.id}`}>
          <Check size={14} weight="bold"/>
        </button>
        <button type="button" onClick={()=>{ setEdit(false); setVal(guest.email || ""); }}
                title="Abbrechen"
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition">
          <X size={13} weight="bold"/>
        </button>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2" onClick={(e)=>e.stopPropagation()}>
      {hasEmail ? (
        <>
          <a href={`mailto:${guest.email}`}
             className="hover:text-primary hover:underline break-all"
             data-testid={`guest-email-${guest.id}`}>{guest.email}</a>
          <button type="button" onClick={()=>setEdit(true)}
                  title="E-Mail-Adresse ändern"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted transition"
                  data-testid={`guest-email-edit-${guest.id}`}>
            <EnvelopeSimple size={13} weight="bold"/>
          </button>
        </>
      ) : (
        <button type="button" onClick={()=>setEdit(true)}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary italic"
                data-testid={`guest-email-add-${guest.id}`}>
          <EnvelopeSimple size={12} weight="bold"/> E-Mail hinzufügen
        </button>
      )}
    </div>
  );
}
